import React from "react";
import { alpha, createTheme } from "@mui/material/styles";
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from "react-router-dom";

export const CALIBRATE_FONT_FAMILY_DISPLAY = [
  '"Century Gothic"',
  '"CenturyGothic"',
  '"Apple Gothic"',
  '"URW Gothic"',
  "sans-serif",
].join(",");

/** Calibrate accent / subtitle text — Montserrat Regular (Google Fonts). */
export const CALIBRATE_FONT_FAMILY_ACCENT = ['"Montserrat"', "sans-serif"].join(
  ",",
);

export const CALIBRATE_COLORS = {
  primaryBrand: "#185059",
  /** App shell / grey area behind main white content panel */
  chromeBackground: "#F3F4F5",
  dashboardBackground: "#FFFFFF",
  /** Hover State Background — #257885 @ 90% */
  hoverBackground: "rgba(37, 120, 133, 0.9)",
  /** Active State Background — #31A0B1 */
  activeBackground: "#31A0B1",
  iconContainerFill: "#2C6292",
  iconColor: "#E5F6FD",
  navText: "#FFFFFF",
  /** Hero Text — #1F2933 @ 90% */
  heroText: "rgba(31, 41, 51, 0.9)",
  /** Secondary Text — #3C4046 @ 90% */
  secondaryText: "rgba(60, 64, 70, 0.9)",
} as const;

export const CALIBRATE_FONT_FAMILY_UI = [
  "Roboto",
  "system-ui",
  "-apple-system",
  "Segoe UI",
  "Helvetica",
  "Arial",
  "sans-serif",
].join(",");

export const CALIBRATE_RADIUS_MD = 8;

export const CALIBRATE_SURFACE_HAIRLINE = "rgba(0, 0, 0, 0.06)";

declare module "@mui/material/styles" {
  interface Palette {
    calibrate: {
      navHover: string;
      navActive: string;
      iconContainer: string;
      iconOnTeal: string;
      heroTitle: string;
      heroSubtitle: string;
      navText: string;
      topBarShadow: string;
      navRowShadowHover: string;
      navRowShadowActive: string;
      mgmtMenuItemHover: string;
      primarySoft03: string;
      primarySoft04: string;
      primarySoft05: string;
      primarySoft06: string;
      primarySoft10: string;
      primaryBorder30: string;
      surfaceHairline: string;
    };
  }
  interface PaletteOptions {
    calibrate?: Partial<Palette["calibrate"]>;
  }

  interface TypographyVariants {
    calibrateDisplay: React.CSSProperties;
    calibrateAccent: React.CSSProperties;
    /** Century Gothic Regular 14px — top bar, sidebar on teal, footer chrome */
    calibrateShell: React.CSSProperties;
    /** Century Gothic Bold — page titles outside hero (Integrations, UM, etc.) */
    calibratePageTitle: React.CSSProperties;
    /** Montserrat Regular — section subtitles / helper blurbs */
    calibrateSectionSubtitle: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    calibrateDisplay?: React.CSSProperties;
    calibrateAccent?: React.CSSProperties;
    calibrateShell?: React.CSSProperties;
    calibratePageTitle?: React.CSSProperties;
    calibrateSectionSubtitle?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    calibrateDisplay: true;
    calibrateAccent: true;
    calibrateShell: true;
    calibratePageTitle: true;
    calibrateSectionSubtitle: true;
  }
}

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
  const { href, ...other } = props;
  return <RouterLink ref={ref} to={href} {...other} />;
});

const PRD_PRIMARY = CALIBRATE_COLORS.primaryBrand;
const PRD_NAV_HOVER = CALIBRATE_COLORS.hoverBackground;
const PRD_NAV_ACTIVE = CALIBRATE_COLORS.activeBackground;
const PRD_HERO = CALIBRATE_COLORS.heroText;
const PRD_SECONDARY_TEXT = CALIBRATE_COLORS.secondaryText;
const PRD_ICON_CONTAINER = CALIBRATE_COLORS.iconContainerFill;

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: PRD_PRIMARY,
      light: PRD_NAV_ACTIVE,
      dark: "#0f383e",
      contrastText: "#ffffff",
    },
    secondary: {
      main: PRD_ICON_CONTAINER,
      light: "#5a8ab8",
      dark: "#1e4466",
      contrastText: "#ffffff",
    },
    background: {
      paper: CALIBRATE_COLORS.dashboardBackground,
      default: CALIBRATE_COLORS.chromeBackground,
    },
    action: {
      hover: "rgba(37, 120, 133, 0.08)",
      selected: "rgba(49, 160, 177, 0.14)",
      disabled: "rgba(0, 0, 0, 0.38)",
      disabledBackground: "rgba(0, 0, 0, 0.10)",
      focus: "rgba(24, 80, 89, 0.18)",
    },
    divider: "rgba(0,0,0,0.12)",
    text: {
      primary: PRD_HERO,
      secondary: PRD_SECONDARY_TEXT,
    },
    error: {
      main: "#D32F2F",
    },
    calibrate: {
      navHover: PRD_NAV_HOVER,
      navActive: PRD_NAV_ACTIVE,
      iconContainer: PRD_ICON_CONTAINER,
      iconOnTeal: CALIBRATE_COLORS.iconColor,
      heroTitle: PRD_HERO,
      heroSubtitle: PRD_SECONDARY_TEXT,
      navText: CALIBRATE_COLORS.navText,
      topBarShadow: "0px 4px 8px rgba(0, 0, 0, 0.25)",
      mgmtMenuItemHover: "rgba(49, 129, 159, 0.1)",
      navRowShadowHover: "0px 4px 4px rgba(0, 0, 0, 0.4)",
      navRowShadowActive: "0px 2px 6px rgba(0, 0, 0, 0.45)",
      primarySoft03: alpha(PRD_PRIMARY, 0.03),
      primarySoft04: alpha(PRD_PRIMARY, 0.04),
      primarySoft05: alpha(PRD_PRIMARY, 0.05),
      primarySoft06: alpha(PRD_PRIMARY, 0.06),
      primarySoft10: alpha(PRD_PRIMARY, 0.1),
      primaryBorder30: alpha(PRD_PRIMARY, 0.3),
      surfaceHairline: CALIBRATE_SURFACE_HAIRLINE,
    },
  },

  shape: {
    borderRadius: CALIBRATE_RADIUS_MD,
  },

  typography: {
    fontFamily: CALIBRATE_FONT_FAMILY_UI,
    fontWeightLight: 300,
    calibrateDisplay: {
      fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
      fontWeight: 700,
      letterSpacing: -0.02,
      lineHeight: 1.2,
    },
    calibrateAccent: {
      fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
      fontWeight: 400,
      letterSpacing: 0.03,
      lineHeight: 1.3,
    },
    calibrateShell: {
      fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
      fontSize: 14,
      fontWeight: 400,
      letterSpacing: -0.02,
      lineHeight: 1.2,
    },
    calibratePageTitle: {
      fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
      fontWeight: 700,
      fontSize: "1.5rem",
      letterSpacing: -0.02,
      lineHeight: 1.2,
      color: PRD_HERO,
    },
    calibrateSectionSubtitle: {
      fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
      fontWeight: 400,
      fontSize: 14,
      letterSpacing: 0.02,
      lineHeight: 1.5,
      color: PRD_SECONDARY_TEXT,
    },
    h3: {
      fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
      fontWeight: 700,
      letterSpacing: -0.02,
    },
    h4: {
      fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
      fontSize: "2.25rem",
      fontWeight: 700,
      letterSpacing: -0.02,
    },
    h5: {
      fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
      fontSize: "1.5rem",
      fontWeight: 700,
      letterSpacing: -0.02,
    },
    h6: {
      fontFamily: CALIBRATE_FONT_FAMILY_DISPLAY,
      fontSize: "1.125rem",
      fontWeight: 700,
    },
    body1: { fontSize: "1rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    button: {
      fontFamily: CALIBRATE_FONT_FAMILY_UI,
      fontWeight: 600,
      letterSpacing: 0,
      textTransform: "none",
    },
  },

  shadows: [
    "none",
    "0px 1px 2px rgba(0,0,0,0.06)",
    "0px 1px 3px rgba(0,0,0,0.12), 0px 1px 2px rgba(0,0,0,0.08)",
    "0px 6px 18px rgba(0,0,0,0.10)",
    "0px 10px 30px rgba(0,0,0,0.12)",
    "0px 14px 40px rgba(0,0,0,0.14)",
    "0px 18px 54px rgba(0,0,0,0.16)",
    "0px 22px 70px rgba(0,0,0,0.18)",
    "0px 12px 40px rgba(0,0,0,0.14), 0px 4px 12px rgba(0,0,0,0.08)",
    "0px 30px 100px rgba(0,0,0,0.22)",
    ...Array.from({ length: 15 }, () => "0px 30px 100px rgba(0,0,0,0.22)"),
  ] as any,

  components: {
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },

    MuiLink: {
      defaultProps: {
        underline: "hover",
        component: LinkBehavior,
      },
      styleOverrides: {
        root: {
          fontWeight: 600,
          textUnderlineOffset: "3px",
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: "primary",
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          "@media (min-width:0px)": {
            "@media (orientation: landscape)": {
              minHeight: 64,
            },
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: CALIBRATE_RADIUS_MD,
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: CALIBRATE_RADIUS_MD,
          border: `1px solid ${CALIBRATE_SURFACE_HAIRLINE}`,
          boxShadow:
            "0px 1px 3px rgba(0,0,0,0.12), 0px 1px 2px rgba(0,0,0,0.08)",
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: CALIBRATE_RADIUS_MD,
          backgroundImage: "none",
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: CALIBRATE_RADIUS_MD,
          paddingInline: 16,
          paddingBlock: 10,
          fontFamily: CALIBRATE_FONT_FAMILY_UI,
        },
        containedPrimary: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 4px 12px rgba(24,80,89,0.25)",
            backgroundColor: PRD_NAV_HOVER,
          },
        },
        outlined: {
          borderWidth: 1,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "medium",
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: CALIBRATE_RADIUS_MD,
          backgroundColor: "rgba(255,255,255,0.9)",
          transition: "box-shadow 140ms ease, border-color 140ms ease",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(24,80,89,0.35)",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 4px rgba(24,80,89,0.12)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(24,80,89,0.55)",
            borderWidth: 1,
          },
        },
        input: {
          paddingTop: 12,
          paddingBottom: 12,
        },
        notchedOutline: {
          borderColor: "rgba(0,0,0,0.12)",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: CALIBRATE_RADIUS_MD,
          border: "1px solid rgba(0,0,0,0.2)",
          boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
          backgroundColor: "#ffffff",
          backgroundImage: "none",
          overflow: "hidden",
        },
        list: {
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          marginLeft: 6,
          marginRight: 6,
          marginTop: 2,
          marginBottom: 2,
          fontSize: 14,
          fontFamily: theme.typography.fontFamily,
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(0,0,0,0.1)",
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
  },
});
